import {
  type ConsolesPalette,
  type ConsolesPaletteOptions,
  type ConsolesTheme,
  type ConsolesThemeOptions,
  darkBaseTheme,
  lightBaseTheme
} from '@img-arena/consoles-base-theme'
import { SxProps, Theme as MuiTheme } from '@mui/material'
import { createTheme } from '@mui/material/styles'
import { deepmerge } from '@mui/utils'
import { DevicePlatformKey } from '@/types/devicePlatform'
import { Sport } from '@/types/sport'

import { commonOptions } from './commonOptions'
import { darkOptions } from './darkOptions'
import { lightOptions } from './lightOptions'

interface TableRowStylesOverrides {
  backgroundColor: string
  'td:first-of-type': SxProps
  'th:first-of-type': SxProps
}

declare module '@mui/material/styles' {
  // TODO: look into every custom property and remove it or replace it with a more generic one
  interface Palette extends ConsolesPalette {
    textColour: string // TODO: remove this
    breadCrumbBackground: string // TODO: remove this
    breadCrumbTextColor: string // TODO: remove this
    breadCrumbBorder: string // TODO: remove this
    operatorTileBackground: string // TODO: remove this
    operatorTileText: string // TODO: remove this
    operatorTileTextColor: string // TODO: remove this
    operatorActionsText: string // TODO: remove this
    accordionHeaderBackground: string // TODO: remove this
    contentContainerBackground: string // TODO: remove this
    matchStatsTableTextColor: string // TODO: remove this
    matchStatsTableSecondaryTextColor: string // TODO: remove this
    inputLabel: string // TODO: remove this
    accordion: {
      // TODO: remove this
      sportTitle: string // TODO: remove this
      tournament: string // TODO: remove this
      match: string // TODO: remove this
      divider: string // TODO: remove this
      matchStatsInnerBackground: string // TODO: remove this
    }
    header: {
      // TODO: remove this
      topBar: {
        // TODO: remove this
        color: string // TODO: remove this
        backgroundColor: string // TODO: remove this
      }
      titleBar: {
        // TODO: remove this
        color: string // TODO: remove this
        backgroundColor: string // TODO: remove this
      }
    }
    subtable: {
      headerRow: TableRowStylesOverrides
      contentRow: TableRowStylesOverrides
    }
    sportColors: {
      [key in Uppercase<Sport> | 'OTHER']: {
        color: string
        background: string
      }
    }
    platformColors: {
      [key in DevicePlatformKey]: {
        color: string
        background: string
      }
    }
    card: {
      header: {
        minHeight: number
        backgroundColor: string
      }
    }
    statistics: {
      panel: {
        backgroundColor: string
        header: {
          minHeight: number
          backgroundColor: string
        }
      }
      card: {
        backgroundColor: string
      }
    }
    charts: {
      lineChart: {
        component: {
          '.MuiAreaElement-root': {
            opacity: number
          }
          '.MuiLineElement-root': {
            opacity: number
          }
        }
        yAxis: {
          color: readonly [string, string]
        }
      }
    }
    phoneInput: {
      buttonBackground: string
      hoverButtonBackground: string
      flagBorder: string
    }
    horizontalTable: {
      header: {
        backgroundColor: string
      }
      border: {
        color: string
      }
    }
    productionReport: {
      actionButton: {
        backgroundColor: string
        hoverBackgroundColor: string
      }
    }
    dateRangePicker: {
      borderRadius: number
      rangeBackground: string
    }
    golfColours: {
      eagle: string
      birdie: string
      bogey: string
      doubleBogey: string
    }
  }
  interface Theme extends ConsolesTheme {
    palette: Palette
  }
  // TODO: look into every custom property and remove it or replace it with a more generic one
  interface PaletteOptions extends ConsolesPaletteOptions {
    textColour?: string // TODO: remove this
    breadCrumbBackground?: string // TODO: remove this
    breadCrumbTextColor?: string // TODO: remove this
    breadCrumbBorder?: string // TODO: remove this
    operatorTileBackground?: string // TODO: remove this
    operatorTileText?: string // TODO: remove this
    operatorTileTextColor?: string // TODO: remove this
    operatorActionsText?: string // TODO: remove this
    accordionHeaderBackground?: string // TODO: remove this
    contentContainerBackground?: string // TODO: remove this
    matchStatsTableTextColor?: string // TODO: remove this
    matchStatsTableSecondaryTextColor?: string // TODO: remove this
    inputLabel?: string // TODO: remove this
    accordion?: {
      // TODO: remove this
      sportTitle?: string // TODO: remove this
      tournament?: string // TODO: remove this
      match?: string // TODO: remove this
      divider?: string // TODO: remove this
      matchStatsInnerBackground?: string // TODO: remove this
    }
    header?: {
      // TODO: remove this
      topBar?: {
        // TODO: remove this
        color?: string // TODO: remove this
        backgroundColor?: string // TODO: remove this
      }
      titleBar?: {
        // TODO: remove this
        color?: string // TODO: remove this
        backgroundColor?: string // TODO: remove this
      }
    }
    subtable?: {
      headerRow?: TableRowStylesOverrides
      contentRow?: TableRowStylesOverrides
    }
    sportColors?: DeepPartial<Palette['sportColors']>
    platformColors?: DeepPartial<Palette['platformColors']>
    card?: DeepPartial<Palette['card']>
    statistics?: DeepPartial<Palette['statistics']>
    charts?: DeepPartial<Palette['charts']>
    phoneInput?: DeepPartial<Palette['phoneInput']>
    horizontalTable?: DeepPartial<Palette['horizontalTable']>
    productionReport?: DeepPartial<Palette['productionReport']>
    dateRangePicker?: DeepPartial<Palette['dateRangePicker']>
    golfColours?: DeepPartial<Palette['golfColours']>
  }
  interface ThemeOptions extends ConsolesThemeOptions {
    palette?: PaletteOptions
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    dashed: true
  }
}

export type Theme = MuiTheme

export const lightMode = deepmerge(lightBaseTheme, deepmerge(commonOptions, lightOptions))
export const darkMode = deepmerge(darkBaseTheme, deepmerge(commonOptions, darkOptions))

export const lightTheme = createTheme(lightMode)
export const darkTheme = createTheme(darkMode)
